import React from "react";
import EmptyState from "./components/EmptyState";
import LoadingState from "./components/LoadingState";
import fields from "./fields";
import utilities from "./UtilityFunctions";
import { visualFor } from "./cellVisuals";

const baseUrl = "https://app.autodealerug.com";

//Money and counts read better right-aligned; dates and booleans don't.
const NUMERIC_TYPES = new Set(["decimal", "integer", "number"]);

// Single memoized row. `isSelected` is a boolean (not the parent's selectedRow)
// so flipping the selection re-renders only the 2 affected rows, not all N.
// Parent callbacks are passed as stable wrappers (see CommonTBody) so memo holds.
const SheetRow = React.memo(function SheetRow({
    rowObj,
    rowindex,
    fieldOrder,
    formattingRules,
    rowFormattingRule,
    sheetName,
    rowActions,
    primaryKeyName,
    isSelected,
    isChecked,
    onToggleCheck,
    onSelect,
    onOpenEdit,
}) {
    const rowStyle = rowFormattingRule ? rowFormattingRule(rowObj) : undefined;
    const rowBg = rowStyle?.backgroundColor ? { backgroundColor: rowStyle.backgroundColor } : {};

    return (
        <tr
            onClick={() => onSelect(rowObj[primaryKeyName])}
            onDoubleClick={() => onOpenEdit(rowindex)}
            className={isSelected ? "selected-row" : ""}
            style={{ cursor: "pointer", ...rowStyle }}
        >
            {/* isChecked is a boolean, not the Set — same reason as isSelected
                above. Passing the Set would change identity on every tick and
                re-render all N rows instead of the one. onToggleCheck being
                undefined is what turns the whole column off. */}
            {onToggleCheck && (
                <td
                    className="sheet-check-cell align-middle"
                    style={rowBg}
                    onClick={(e) => e.stopPropagation()}
                    onDoubleClick={(e) => e.stopPropagation()}
                >
                    <input
                        type="checkbox"
                        aria-label={`Select row ${rowindex + 1}`}
                        checked={isChecked}
                        onChange={() => onToggleCheck(rowObj[primaryKeyName])}
                    />
                </td>
            )}
            {
                // Inventory/Committed/Cash/Credit/CreditCleared lead with PrimaryImage
                ["Inventory", "Committed", "Cash", "Credit", "CreditCleared"].includes(sheetName) && (
                    <td className="sheet-thumb-cell col-1 align-middle" style={rowBg}>
                        {rowObj.PrimaryImage && typeof rowObj.PrimaryImage === "string" ? (
                            <div className="sheet-thumb relative overflow-hidden rounded-md bg-gray-100 flex items-center justify-center">
                                <img
                                    src={`${baseUrl}${rowObj.PrimaryImage.replace(/\.jpg$/i, "_64.jpg")}`}
                                    alt="Primary"
                                    className="object-cover h-full w-full"
                                    onError={(e) => {
                                        const src = e.target.src;
                                        if (src.includes("_64.jpg")) {
                                            e.target.src = `${baseUrl}${rowObj.PrimaryImage}`;
                                        } else {
                                            e.target.onerror = null;
                                            e.target.src = "https://app.autodealerug.com/images/cars/placeholder.png";
                                        }
                                    }}
                                />
                            </div>
                        ) : (
                            <div className="sheet-thumb flex items-center justify-center rounded-md bg-gray-200">
                                <svg className="h-6 w-6 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                </svg>
                            </div>
                        )}
                    </td>
                )
            }
            {fieldOrder.map((fieldObj) => {
                //Optional-chained to match CommonThead/CommonDataView — an unknown
                //column (SELECT * picking up a new DB field) skips, it doesn't crash.
                if (fields[fieldObj.name]?.public && !fieldObj.hidden) {
                    // Inline visual takes precedence; when it renders, skip the
                    // formattingRules text-color for that cell (but keep rowBg).
                    // visualFor knows which sheets each field's visual belongs on.
                    const visualFn = visualFor(fieldObj.name, sheetName);
                    const visual = visualFn ? visualFn(rowObj[fieldObj.name], rowObj, sheetName) : null;

                    let format = { ...rowBg };
                    if (visual == null && formattingRules && formattingRules[fieldObj.name]) {
                        format = { ...format, ...formattingRules[fieldObj.name].formatByValue(rowObj[fieldObj.name]) };
                    }
                    //Right-align plain numeric cells so magnitudes line up. Cells
                    //carrying an inline visual (bars/dials) keep their own layout.
                    const numeric = visual == null && NUMERIC_TYPES.has(fields[fieldObj.name].dataType);
                    return (
                        <td key={fieldObj.name} className={`col-${fieldObj.name} align-middle`}
                            data-numeric={numeric ? "true" : undefined} style={format}>
                            {visual != null
                                ? visual
                                : utilities.formatIntoInput(rowObj[fieldObj.name], fields[fieldObj.name].dataType)}
                        </td>
                    );
                }
                return null;
            })}
            {rowActions && (
                <td
                    className="align-middle"
                    style={{ width: "2rem", padding: "0 0.25rem", ...rowBg }}
                    // Stop row selection / double-click edit from firing here
                    onClick={(e) => e.stopPropagation()}
                    onDoubleClick={(e) => e.stopPropagation()}
                >
                    {rowActions(rowObj)}
                </td>
            )}
        </tr>
    );
});

function CommonTBody({
    isLoading = false,
    objectsArray,
    selectRow,
    selectedRow,
    fieldOrder,
    openModalEdit,
    primaryKeyName,
    formattingRules,
    rowFormattingRule,
    sheetName = null,
    // Optional render-prop: (rowObj) => ReactNode, appended as a trailing <td>.
    rowActions = null,
    // Optional multi-select (useRowSelection). Absent on every sheet that has
    // not opted in, and then the column does not render at all.
    selection = null,
}) {
    // Parent callbacks aren't referentially stable (recreated by sheet
    // components each render). Hold the latest in a ref and hand SheetRow
    // stable wrappers so memoization isn't defeated on every parent render.
    const callbacksRef = React.useRef({ selectRow, openModalEdit, selection });
    callbacksRef.current = { selectRow, openModalEdit, selection };

    const onSelect = React.useCallback((pk) => callbacksRef.current.selectRow(pk), []);
    const onOpenEdit = React.useCallback((idx) => callbacksRef.current.openModalEdit(idx), []);
    const onToggleCheck = React.useCallback((pk) => callbacksRef.current.selection?.toggle(pk), []);

    return (
        <tbody id="main-table-body">
            {objectsArray.length > 0 ? (
                objectsArray.map((rowObj, rowindex) => (
                    <SheetRow
                        key={rowindex}
                        rowObj={rowObj}
                        rowindex={rowindex}
                        fieldOrder={fieldOrder}
                        formattingRules={formattingRules}
                        rowFormattingRule={rowFormattingRule}
                        sheetName={sheetName}
                        rowActions={rowActions}
                        primaryKeyName={primaryKeyName}
                        isSelected={rowObj[primaryKeyName] == selectedRow}
                        isChecked={!!selection?.ids.has(rowObj[primaryKeyName])}
                        onToggleCheck={selection ? onToggleCheck : undefined}
                        onSelect={onSelect}
                        onOpenEdit={onOpenEdit}
                    />
                ))
            ) : (
                <tr>
                    <td colSpan={fieldOrder.length + (selection ? 1 : 0)} className="text-center align-middle">
                        {isLoading ? <LoadingState /> : <EmptyState />}
                    </td>
                </tr>
            )}
        </tbody>
    );
}

export default CommonTBody;
